import { createSelector } from 'reselect';
import RootState from 'ducks/RootState';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import { getRestrictionsGeoCollection } from '../restrictions/selectors';

export const getItemsState = (state: RootState) => state.items;

export const getItemsCollection = (state: RootState) => getItemsState(state).collection;

export const getItemsTotal = (state: RootState) => getItemsState(state).total;

export const getItemDetails = (state: RootState) => getItemsState(state).itemDetails;

export const getItemId = (state: RootState) => getItemDetails(state).id;

export const getItemAccessFees = (state: RootState) => getItemDetails(state).accessFees;

export const getItemAccessTypes = (state: RootState) => getItemsState(state).accessTypes;

export const getJwPlayers = (state: RootState) => getItemsState(state).jwPlayers;

export const getOvpVideoDetails = (state: RootState) => getItemsState(state).ovpVideoDetails;

// access codes
export const getItemCodes = (state: RootState) => getItemsState(state).codes;

export const getItemCodesReports = (state: RootState) => getItemCodes(state).reports;

export const getCodeSessions = (state: RootState) => getItemCodes(state).codeSessions;

export const getBulkCodesErrorFiles = (state: RootState) =>
  orderBy(getItemsState(state).codes.errorFiles.files, ['createdAt'], ['desc']);

export const getIsFetchingErrorFiles = (state: RootState) => getItemsState(state).codes.errorFiles.isFetching;

export const getIsFileUploaded = (state: RootState) => getItemsState(state).codes.isUploaded;

// donations
export const getDonationDetails = (state: RootState) => getItemDetails(state).donationDetails;

export const getDonationOptions = (state: RootState) => getDonationDetails(state).donations ?? [];

export const getDonationCustomOption = (state: RootState) => getDonationDetails(state).donation_options;

export const getIsDonationCustomPriceEnabled = (state: RootState) =>
  getDonationCustomOption(state)?.custom_price_enabled || false;

export const getIsDonationLoading = (state: RootState) => getDonationDetails(state).isLoading;

// templates
export const getTemplates = (state: RootState) => getItemsState(state).templates;

export const getTemplateCollection = (state: RootState) => getTemplates(state).templateCollection.collection;

export const getTemplateCollectionFetching = (state: RootState) => getTemplates(state).templateCollection.isFetching;

export const getTemplateRestrictionsInOriginalFormat = (state: RootState) =>
  getTemplates(state).templateDetails.restrictions;

export const getTemplateDetails = (state: RootState) => {
  const temp = getTemplates(state).templateDetails;
  const { name, prices, access_control_type_id, restrictions, id: templateId, merchant_id, ...otherProps } = temp;

  const accessTypes = getItemAccessTypes(state);

  const pricesWithAccessTypeDetails = prices?.map((price) => {
    const ppvType = accessTypes.ppv.find((type) => Number(type.value) === price.access_type_id);
    const subsType = accessTypes.subscriptions.find((type) => Number(type.value) === price.access_type_id);
    const seasonType = accessTypes.season.find((type) => Number(type.value) === price.access_type_id);
    let type: any;
    let typeName = '';
    let formattedPriceObject = {
      ...price,
      starts_at: moment(price?.starts_at).unix() || '',
      expires_at: moment(price?.expires_at).unix() || '',
    };

    if (price.country_iso || price.country_set_id) {
      formattedPriceObject = {
        ...formattedPriceObject,
        geo_restriction: {
          country_iso: price.country_iso || '',
          country_set_id: price.country_set_id || '',
          id: price.id,
          type: price.type,
          updated_at: price.updated_at,
        },
      };
    }

    if (price.setup_fee_amount && price.setup_fee_description) {
      formattedPriceObject = {
        ...formattedPriceObject,
        setup_fee: {
          created_at: price.created_at,
          id: price.id,
          updated_at: price.updated_at,
          fee_amount: price.setup_fee_amount,
          description: price.setup_fee_description,
        },
      };
    }

    if (ppvType !== undefined || subsType !== undefined) {
      if (ppvType !== undefined) {
        type = ppvType;
        typeName = ppvType.value === '21' ? 'ppv_custom' : 'ppv';
      } else if (seasonType !== undefined) {
        type = seasonType;
        typeName = 'season';
      } else {
        type = subsType;
        typeName = 'subscription';
      }
    }

    if (type) {
      const splitted = type?.displayName.split(' ');

      formattedPriceObject = {
        ...formattedPriceObject,
        access_type: {
          ...type,
          name: typeName,
          id: Number(type.value),
          quantity: splitted[0],
          period: splitted[1] ?? splitted[0],
        },
      };
    }

    return formattedPriceObject;
  });
  const assetDomainRestrictions: any = [];
  const inputArray: any[] = [];

  let restrictionObj = {
    ageRestriction: { age: -1, id: -1 },
    assetCountrySet: {},
    assetDomainsInputs: { inputs: inputArray },
  };

  if (restrictions !== null) {
    restrictions.forEach((restriction) => {
      const { category, additional_data, id } = restriction;

      if (category === 'age') {
        restrictionObj = {
          ...restrictionObj,
          ageRestriction: {
            age: Number(additional_data),
            id,
          },
        };
      }

      if (category === 'domain') {
        assetDomainRestrictions.push({
          id,
          item_id: templateId,
          merchant_id,
          domain: additional_data,
          type: 'whitelist',
        });

        restrictionObj = {
          ...restrictionObj,
          assetDomainsInputs: {
            inputs: [...assetDomainRestrictions],
          },
        };
      }

      if (category === 'geo') {
        const globalRestrictionsGeoCollection = getRestrictionsGeoCollection(state);

        const geoDetails = globalRestrictionsGeoCollection.find((item) => item.id === Number(additional_data));

        if (geoDetails) {
          const { name: geoDetailsName, type, global } = geoDetails;

          restrictionObj = {
            ...restrictionObj,
            assetCountrySet: {
              id: Number(additional_data),
              created_at: restriction.created_at,
              type,
              name: geoDetailsName,
              global,
              merchant_id,
            },
          };
        }
      }
    });
  }

  const mappedTemplateDetails = {
    title: name,
    accessFees: pricesWithAccessTypeDetails,
    content: '',
    template_id: null, // preview template id
    restrictions: restrictionObj,
    access_control_type: { id: access_control_type_id },
    ...otherProps,
  };

  return mappedTemplateDetails;
};

export const getSelectedTemplateFeeId = (state: RootState) => getTemplateDetails(state).selectedTemplateFeeId;

export const getSelectedTemplateFee = (state: RootState) => {
  const templateDetails = getTemplateDetails(state);

  return templateDetails?.accessFees?.find((fee) => fee.id === templateDetails?.selectedTemplateFeeId);
};

// livelike
export const getItemLiveLikeResources = (state: RootState) => getItemDetails(state).external_integrations.livelike;
export const getLiveLikeResources = (state: RootState) => getItemsState(state).livelikeResources;
export const getChatRooms = (state: RootState) => getLiveLikeResources(state).chatRooms.collection;
export const getChatRoomIsCreating = (state: RootState) => getLiveLikeResources(state).chatRooms.isCreating;
export const getChatRoomStatus = (state: RootState) => getLiveLikeResources(state).chatRooms;
export const getSavedChatroomDetails = createSelector([getLiveLikeResources], (resources) => resources.savedChatroom);
export const getItemChatRoom = createSelector(
  getItemLiveLikeResources,
  ({ chatrooms: [selectedChatroom] }) => selectedChatroom
);

// ivs ovp
export const getIvsChannelsState = createSelector(getItemsState, (items) => items.ovps.ivs.channels);
export const getIvsChannelsStatus = createSelector(getIvsChannelsState, (channels) => channels.status);
export const getIvsChannelsList = createSelector(getIvsChannelsState, (channels) => channels.list);
export const getIvsChannelsData = createSelector([getIvsChannelsStatus, getIvsChannelsList], (status, list) => ({
  status,
  list,
}));

// subscription plan
export const getIsPlanSwitchEnabled = createSelector(
  getItemsState,
  ({ itemDetails }) => itemDetails.plan_switch_enabled
);
export const getTotalSubscriptions = createSelector(
  getItemAccessFees,
  (accessFees) => accessFees.filter((accessFee) => accessFee.access_type.name === 'subscription').length
);
